import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CbyIsfStepComponent } from '../cby-isf-step-component/cby-isf-step.component';
import { EntityModel } from '@data-layer/models/entities';
import { CompanyModel } from '@data-layer/models/companies';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { CompaniesService } from '@services/companies.service';
import { enumEntityByKey, enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumIsfDesktopCodeStep } from '@app/modules/isf/enums';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { enumDocumentType } from '@data-layer/enums/documents';
import { ALLOWED_FILE_MIMES } from '@constants/constants';
import { IsfModel } from '@data-layer/models/isf';
import { BondModel, BondRequestModel, ShipmentModel } from '@data-layer/models/common';
import { IsfDetailsValidationService, IsfStateService } from '../../services';
import { enumIsfShipmentCode, enumIsfShipmentType } from '@app/data-layer/enums/isf';
import { IsfHelper } from '@helpers/isf.helper';
import { DesktopShipmentDetailsHtsComponent } from '../desktop-shipment-details/desktop-shipment-details-hts/desktop-shipment-details-hts.component';
import { take, takeUntil } from 'rxjs/operators';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { DocumentsService } from '@services/documents.service';
import { MarineInsuranceSideformService, MarineInsuranceState } from '@shared/marine-insurance-shared/services';
import { enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { DocumentModel } from '@data-layer/models/documents';
import { enumPaymentState } from '@app/data-layer/enums/payments';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { GenericService } from '@services/generic.service';

@Component({
  selector: 'desktop-shipment-details',
  template: `
    <div>
      <h1 class="step-header">{{ 'LABELS.SHIPMENT_INFO' | translate }}</h1>
      <span class="step-hint">{{ 'LABELS.SHIPMENT_INFO_HINT' | translate }}</span>
    </div>

    <div class="mt-3 mb-3">
      <isf-data-card
        cardTitle="LABELS.SHIPMENT_DETAILS"
        imgSrc="assets/icons/vessel.svg"
        [dataIsValid]="isf?.type === enumIsfShipmentType.FORMAL ? isFormalValid : isInformalValid"
      >
        <desktop-shipment-details-type
          body
          [invoices]="invoices"
          (formValidityUpdated)="setValidityOfForm($event, 'isValidFormType')"
        ></desktop-shipment-details-type>
      </isf-data-card>

      <isf-data-card cardTitle="LABELS.BILL_OF_LADING_DETAILS" imgSrc="assets/icons/bond.svg" [dataIsValid]="isValidShipment">
        <desktop-shipment-details-bl
          body
          (saveBLData)="onSaveBLData($event)"
          (formValidityUpdated)="setValidityOfForm($event, 'isValidFormBL')"
        ></desktop-shipment-details-bl>
      </isf-data-card>

      <isf-data-card cardTitle="LABELS.INTERNATIONAL_HTS_CODE" imgSrc="assets/icons/hts.svg" [dataIsValid]="isValidHTS">
        <desktop-shipment-details-hts
          body
          [manufacturers]="manufacturers"
          [invoices]="invoices"
          [isSaveOnParentBtn]="true"
          (editMF)="edit.emit($event)"
          (dataSaved)="loadData()"
          (formValidityUpdated)="setValidityOfForm($event, 'isValidFormHTS')"
          (openValidationModal)="missingFieldsComponent.openCustomModal($event)"
        ></desktop-shipment-details-hts>

        <div class="isf-data-card-footer" footer *ngIf="!manufacturers.length">
          <button type="button" class="btn btn-link" (click)="useDifferent.emit(enumEntityModelCode.MANUFACTURER)">
            <i class="fa fa-plus-circle"></i>
            <span>{{ 'LABELS.ADD_MANUFACTURER' | translate }}</span>
          </button>
        </div>
      </isf-data-card>

      <ng-container *ngIf="!(isInsuranceLoading | async)">
        <app-marine-insurance-quote-view-card
          *ngIf="!(isInsuranceAvailable | async)"
          [marineInsurance]="currentInsurance | async"
          [documents]="currentInsuranceDocuments | async"
          [entity]="enumMarineInsuranceEntityType.ISF"
          [state]="isf.state"
          [isShowUpdated]="
            !!(currentInsurance | async)?.updatedInsuredValue?.insuredAmount &&
            (!(currentInsurancePendingBills | async)?.items?.length || (currentInsurance | async)?.partiallyRefundConfirmed)
          "
          (edit)="openMarineInsuranceQuoteForm(true)"
        ></app-marine-insurance-quote-view-card>
      </ng-container>

      <desktop-summary-upload-isf-documents
        *ngIf="!isHasIsfOcr"
        [company]="company"
        [isf]="isf"
        (downloadAllFiles)="onDownloadAllFiles($event)"
      ></desktop-summary-upload-isf-documents>
    </div>

    <missing-fields [code]="'LABELS.INTERNATIONAL_HTS_CODE'" (decline)="next()"></missing-fields>
  `,
  styleUrls: ['./desktop-shipment-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopShipmentDetailsComponent extends CbyIsfStepComponent implements OnInit, OnDestroy {
  @Input() public isfEntityCode: enumIsfDesktopCodeStep;
  @Input() public useCompanyId: number;
  @Output() public edit: EventEmitter<{ id: number; entityType: enumEntityModelCode }> = new EventEmitter<{
    id: number;
    entityType: enumEntityModelCode;
  }>();
  @Output() public useDifferent: EventEmitter<enumEntityModelCode> = new EventEmitter<enumEntityModelCode>();

  @ViewChild(DesktopShipmentDetailsHtsComponent, { static: false })
  public desktopShipmentDetailsHtsComponent: DesktopShipmentDetailsHtsComponent;
  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldsComponent: MissingFieldsComponent;

  public isf: IsfModel;
  public shipmentBLFormData: ShipmentModel;
  public importer: EntityModel;

  public manufacturers: EntityModel[];
  public invoices: InvoiceModel[];

  public enumEntityModelCode = enumEntityModelCode;
  public enumEntityByKey = enumEntityByKey;
  public enumDocumentType = enumDocumentType;
  public ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  public enumIsfShipmentType: typeof enumIsfShipmentType = enumIsfShipmentType;
  public enumPaymentState: typeof enumPaymentState = enumPaymentState;

  public company: CompanyModel;

  public bond: BondModel;
  public bondRequest: BondRequestModel;
  public shipment: ShipmentModel;

  public isValidFormType: boolean;
  public isValidFormBL: boolean;

  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  public enumMarineInsuranceEntityType: typeof enumMarineInsuranceEntityType = enumMarineInsuranceEntityType;

  private isMobile: boolean;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private genericService: GenericService,
    public isfStateService: IsfStateService,
    private changeDetector: ChangeDetectorRef,
    private companiesService: CompaniesService,
    private isfDetailsValidationService: IsfDetailsValidationService,
    private documentsService: DocumentsService,
    private marineInsuranceState: MarineInsuranceState,
    private marineInsuranceSideformService: MarineInsuranceSideformService,
  ) {
    super();
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.loadData(true);

    this.genericService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
      this.changeDetector.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public checkValidState(): void {}

  public continue(): void {
    // save hts codes, then save the rest data
    this.desktopShipmentDetailsHtsComponent.saveOnButtonClick(true);

    this.desktopShipmentDetailsHtsComponent.dataSaved.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isfStateService
        .updateShipment(this.shipmentBLFormData)
        .pipe(take(1))
        .subscribe((shipment) => {
          this.isfStateService.currentIsf.shipment = shipment;
          this.next();
        });
    });
  }

  public skip(): void {}

  public loadData(loadShipment?: boolean): void {
    this.company = this.companiesService.getCompany();
    this.isf = this.isfStateService.currentIsf;
    if (loadShipment) {
      this.shipmentBLFormData = this.isf.shipment;
    }

    this.importer = !!this.isfStateService.currentIsf
      ? this.isfStateService.getIsfEntityById(enumEntityByKey.IMPORTER)
      : this.isfStateService.initImporterFromCompany();

    this.manufacturers = this.isfStateService.getIsfManufacturerEntityList();
    this.invoices = this.isfStateService.getIsfInvoicesList();

    this.bond = IsfHelper.getBond(this.isf);
    this.bondRequest = IsfHelper.getBondRequest(this.isf);

    this.shipment = IsfHelper.getFullShipment(this.isf);

    // detect changes due to componentRef is not updating it
    this.changeDetector.detectChanges();
  }

  public onSaveBLData(data: ShipmentModel) {
    this.shipmentBLFormData = data;
  }

  public get isInformalValid(): boolean {
    return !this.isfDetailsValidationService.hasInformalEstimationError(this.isf) && this.isValidFormType;
  }

  public get isFormalValid(): boolean {
    return (!!this.bond || !!this.bondRequest) && this.isValidFormType;
  }

  public get isValidShipment(): boolean {
    if (this.shipment.code === enumIsfShipmentCode.HOUSE) {
      return !!this.shipment.houseBill.carrier && !!this.shipment.houseBill.number && this.isValidFormBL;
    } else {
      return !!this.shipment.masterBill.carrier && !!this.shipment.masterBill.number && this.isValidFormBL;
    }
  }

  public get isValidHTS(): boolean {
    return this.isf.shipment.invoices.length && this.isf.shipment.invoices.every((invoice) => invoice.lines.length);
  }

  public setValidityOfForm(isValid: boolean, key: string) {
    this[key] = isValid;
  }

  public onDownloadAllFiles(documentsIds: number[]): void {
    this.documentsService
      .getArchive(this.isf.referenceNumber, documentsIds)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, this.documentsService.formatZipFileName(this.isf.referenceNumber));
      });
  }

  public openMarineInsuranceQuoteForm(isEdit?: boolean): void {
    this.marineInsuranceSideformService.onOpenQuoteSidebar(
      enumMarineInsuranceEntityType.ISF,
      {
        isf: this.isfStateService.currentIsf,
      },
      this.marineInsuranceState.currentInsurance.getValue(),
      isEdit,
    );
  }

  public get isInsuranceAvailable(): Observable<boolean> {
    return this.marineInsuranceState.isInsuranceAvailable;
  }

  public get isInsuranceLoading(): Observable<boolean> {
    return this.marineInsuranceState.isInsuranceLoading;
  }

  public get currentInsurance(): BehaviorSubject<MarineInsuranceModel> {
    return this.marineInsuranceState.currentInsurance;
  }

  public get currentInsuranceDocuments(): BehaviorSubject<DocumentModel> {
    return this.marineInsuranceState.currentInsuranceDocuments;
  }

  public get currentInsurancePendingBills(): BehaviorSubject<PaymentBillItemPendingModel> {
    return this.marineInsuranceState.currentInsurancePendingBills;
  }

  public get isHasIsfOcr(): boolean {
    const isWithUserWithIsfOcrFlag = this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.isfOcr);

    return isWithUserWithIsfOcrFlag && !this.isMobile;
  }
}
